import React, { useState, useRef, useCallback, useEffect, useId } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useTelegram } from '../hooks/useTelegram';
import { SBPModal } from './SBPModal';
import { WhiteListNotStartedModal } from './WhiteListNotStartedModal';
import { UpgradeModal } from './UpgradeModal';
import { calcTimeLeft, pad } from '../utils/timer';
import { resolveTelegramBotUsername } from '../utils/telegramBotUsername';
import type { NFTDetailItem, PrizeStatusResponse, NftPurchaseResponse } from '../types';
import { formatPenki, penkiPerDayForNft, pluralPenka } from '../utils/penki';
import './PenkiBadge.css';
import './NFTDetailPage.css';
import './NFTDetailModal.css';

type PurchaseState = 'idle' | 'creating' | 'waiting' | 'success' | 'error';
type PurchaseType = 'purchase' | 'upgrade';

/**
 * Hard deadline for the link-acquisition ("creating") phase. The backend caps the
 * eKazna /pay call at 60s, so by ~75s the purchase row is guaranteed to be either
 * `link_received` (payment_url set) or `failed`. After this the user always gets a
 * clear error instead of an infinite spinner.
 */
const PAYMENT_CREATE_DEADLINE_MS = 75_000;
/** How often the frontend polls the backend for the payment link / failure. */
const PAYMENT_CREATE_POLL_INTERVAL_MS = 2_500;

/**
 * Hard cap on how long we poll eKazna for a payment stuck in a non-final status.
 * Mirrors the backend reconciler (EXPIRE_AFTER_MINUTES = 120): after 2 hours a
 * non-terminal payment is treated as failed and polling stops.
 */
const PAYMENT_STATUS_POLL_DEADLINE_MS = 2 * 60 * 60 * 1000;

/** Per-attempt idempotency token so the frontend can poll for this exact purchase. */
function generateClientToken(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to manual token
  }
  return `ct_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const NFTDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isFromRandom = searchParams.get('random') === '1';
  const { user } = useUser();
  const { tg } = useTelegram();
  const [nft, setNft] = useState<NFTDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showSBPModal, setShowSBPModal] = useState(false);
  const [showWhiteListModal, setShowWhiteListModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [purchaseState, setPurchaseState] = useState<PurchaseState>('idle');
  const [purchaseType, setPurchaseType] = useState<PurchaseType>('purchase');
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollDeadlineRef = useRef<number | null>(null);
  /** Cancellation token for the in-flight link-acquisition loop (reset / unmount). */
  const flowCancelRef = useRef<{ cancelled: boolean } | null>(null);
  const prizeRingUid = useId().replace(/:/g, '');
  const prizeRingClipId = `prize-ring-clip-${prizeRingUid}`;
  const prizeRingInnerGlowId = `prize-ring-inner-glow-${prizeRingUid}`;
  const prizeProgressGradientId = `prize-progress-gradient-${prizeRingUid}`;
  const [claimingPrize, setClaimingPrize] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // Prize claim timer state
  const [claimPhase, setClaimPhase] = useState(0);
  const [claimTimerTarget, setClaimTimerTarget] = useState<number | null>(null);
  /** Full length of the running phase timer (seconds), set per drop in the admin panel */
  const [claimTimerTotalSeconds, setClaimTimerTotalSeconds] = useState<number | null>(null);
  const [isMyClaimActive, setIsMyClaimActive] = useState(false);
  const [prizeAwarded, setPrizeAwarded] = useState(false);
  const [prizeIntercepted, setPrizeIntercepted] = useState(false);
  const [awardedDropNftId, setAwardedDropNftId] = useState<number | null>(null);
  const [prizeImageUrl, setPrizeImageUrl] = useState<string | null>(null);
  const prizePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerClaimAttemptedRef = useRef(false);
  const [timerTick, setTimerTick] = useState(0);
  const [whiteListStatus, setWhiteListStatus] = useState<{ in_white_list: boolean } | null>(null);
  const [opendoorStartDate, setOpendoorStartDate] = useState<Date | null>(null);

  const isOwner = nft?.owner?.id === user?.id;
  const canClaimPrize = isOwner && nft?.has_active_prize_drop;
  const dailyPenki = nft ? penkiPerDayForNft(nft) : 0;

  /** Бэкенд хранит claim_user_id — is_my_claim остаётся true у старого владельца после перекупки; таймер только у текущего владельца */
  const info = nft?.prize_claim_info;
  const exOwnerClaimInterrupted =
    !isOwner &&
    !!info &&
    info.is_my_claim &&
    info.phase > 0 &&
    info.phase < 3 &&
    !prizeAwarded;
  const showPrizeIntercepted = prizeIntercepted || exOwnerClaimInterrupted;

  const now = Date.now();
  const isAfterOpendoor = opendoorStartDate ? now >= opendoorStartDate.getTime() : true;
  /** Покупка только у текущего владельца; на экране «выкуплен» — даже если owner в state ещё не обновился после refetch */
  const canShowBuyButton =
    !!nft?.owner &&
    (isAfterOpendoor || whiteListStatus?.in_white_list === true) &&
    (!isOwner || showPrizeIntercepted) &&
    nft?.can_purchase !== false;
  /** Покупатель, начавший оплату и вернувшийся в приложение, может продолжить покупку */
  const canShowContinueButton = !!nft?.resume_payment;

  const resetPurchaseState = useCallback(() => {
    setPurchaseState('idle');
    setPurchaseError(null);
    setClaimingPrize(false);
    if (flowCancelRef.current) {
      flowCancelRef.current.cancelled = true;
      flowCancelRef.current = null;
    }
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    pollDeadlineRef.current = null;
  }, []);

  /**
   * Poll the payment until the backend reports a terminal outcome. Ownership is
   * transferred server-side once eKazna confirms the payment for this exact order,
   * so a `success` here only mirrors what the backend already did.
   */
  const pollPaymentStatus = useCallback(async (ekaznaPaymentId: string) => {
    const POLL_INTERVAL_MS = 3000;

    // Опрос неконечного статуса ограничен 2 часами (совпадает с бэкендом).
    if (pollDeadlineRef.current !== null && Date.now() >= pollDeadlineRef.current) {
      setPurchaseState('error');
      setPurchaseError('Время ожидания оплаты истекло. Попробуйте ещё раз.');
      return;
    }

    const scheduleNextPoll = () => {
      pollingRef.current = setTimeout(() => {
        pollPaymentStatus(ekaznaPaymentId);
      }, POLL_INTERVAL_MS);
    };

    try {
      const statusResponse = await apiClient.getPaymentStatus(ekaznaPaymentId);
      if (statusResponse.status === 'success') {
        setPurchaseState('success');
        return;
      }
      if (statusResponse.status === 'failed') {
        setPurchaseState('error');
        setPurchaseError('Платёж отклонён');
        return;
      }
      scheduleNextPoll();
    } catch (error) {
      const httpStatus = (error as { response?: { status?: number } })?.response?.status;
      // 403/404/422 mean this payment will never resolve for this user — no point waiting.
      if (httpStatus === 403 || httpStatus === 404 || httpStatus === 422) {
        setPurchaseState('error');
        setPurchaseError('Платёж не найден. Попробуйте ещё раз.');
        return;
      }
      // Transient network / gateway error: keep polling until the deadline.
      scheduleNextPoll();
    }
  }, []);

  /** Switch to the post-link "waiting for payment" state and start status polling. */
  const beginPaymentStatusPolling = useCallback((ekaznaPaymentId: string) => {
    setPurchaseState('waiting');
    pollDeadlineRef.current = Date.now() + PAYMENT_STATUS_POLL_DEADLINE_MS;
    pollingRef.current = setTimeout(() => {
      pollPaymentStatus(ekaznaPaymentId);
    }, 3000);
  }, [pollPaymentStatus]);

  /** Open the SBP link (QR / deep link) and move on to payment-status polling. */
  const openLinkAndWait = useCallback((paymentUrl: string, ekaznaPaymentId: string) => {
    if (tg) {
      tg.openLink(paymentUrl, { try_instant_view: false });
    } else {
      window.open(paymentUrl, '_blank');
    }
    beginPaymentStatusPolling(ekaznaPaymentId);
  }, [tg, beginPaymentStatusPolling]);

  /**
   * Reliable link acquisition: fires the create request but does NOT depend on it.
   * In parallel it polls the backend by client token until one terminal outcome is
   * reached — a payment link, a definitive failure, or a hard deadline — so the
   * "Создание платежа..." spinner can never hang forever (even if the create
   * response is lost while the eKazna gateway is slow).
   */
  const runPaymentFlow = useCallback(async (
    createRequest: Promise<NftPurchaseResponse>,
    clientToken: string,
  ) => {
    const cancel = { cancelled: false };
    if (flowCancelRef.current) flowCancelRef.current.cancelled = true;
    flowCancelRef.current = cancel;

    setPurchaseState('creating');
    setPurchaseError(null);

    // Container (not bare `let`) so TS keeps the declared types across awaits and
    // doesn't narrow the closure-mutated values down to `never`.
    const created: { result: NftPurchaseResponse | null; error: any; done: boolean } = {
      result: null,
      error: null,
      done: false,
    };
    // Attach handlers so the request is best-effort and never an unhandled rejection.
    createRequest
      .then((r) => { created.result = r; })
      .catch((e) => { created.error = e; })
      .finally(() => { created.done = true; });

    const fail = (message: string) => {
      if (cancel.cancelled) return;
      setPurchaseState('error');
      setPurchaseError(message);
    };

    const deadline = Date.now() + PAYMENT_CREATE_DEADLINE_MS;

    while (Date.now() < deadline) {
      if (cancel.cancelled) return;

      // 1) Fast path: the original create response already returned a link.
      const createdResult = created.result;
      if (createdResult?.payment_url && createdResult.ekazna_payment_id && createdResult.payment_id) {
        openLinkAndWait(createdResult.payment_url, createdResult.ekazna_payment_id);
        return;
      }

      // 2) Independent recovery: poll the backend for this exact purchase.
      try {
        const status = await apiClient.getPurchaseStatusByToken(clientToken);
        if (cancel.cancelled) return;
        if (status.found) {
          if (status.payment_url && status.ekazna_payment_id && status.payment_id) {
            openLinkAndWait(status.payment_url, status.ekazna_payment_id);
            return;
          }
          if (status.status === 'failed') {
            fail(status.message || 'Ошибка при создании платежа');
            return;
          }
        }
      } catch {
        // ignore transient poll errors — keep trying until the deadline
      }

      // 3) If the create request finished with a definitive client/validation error
      //    (4xx with a message), no link will ever appear — surface it immediately.
      if (created.done && created.error && !created.result?.payment_url) {
        const httpStatus = created.error?.response?.status;
        const message = created.error?.response?.data?.message;
        if (message && typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500) {
          fail(message);
          return;
        }
        // Network error / timeout / 5xx: the row may still receive a link from a
        // slow gateway, so keep polling until the deadline.
      }

      await sleep(PAYMENT_CREATE_POLL_INTERVAL_MS);
    }

    fail('Платёжный шлюз не ответил вовремя. Попробуйте ещё раз.');
  }, [openLinkAndWait]);

  /** Повторно открыть уже созданную платёжную ссылку (кнопка «Продолжить покупку»). */
  const handleContinuePurchase = useCallback(() => {
    if (!nft?.resume_payment) return;
    const { payment_url, ekazna_payment_id } = nft.resume_payment;
    setPurchaseType('purchase');
    openLinkAndWait(payment_url, ekazna_payment_id);
  }, [nft, openLinkAndWait]);

  useEffect(() => {
    const loadNft = async () => {
      if (!id) {
        setError('NFT не найден');
        setLoading(false);
        return;
      }
      try {
        const detail = await apiClient.getNFTDetail(id);
        setNft(detail);
      } catch {
        setError('Не удалось загрузить NFT');
      } finally {
        setLoading(false);
      }
    };
    loadNft();
  }, [id]);

  useEffect(() => {
    const loadWhiteListInfo = async () => {
      try {
        const [stats, status] = await Promise.all([
          apiClient.getWhiteListStats().catch(() => null),
          apiClient.getWhiteListStatus().catch(() => null),
        ]);
        if (status) setWhiteListStatus(status);
        if (stats?.opendoor_start_date) setOpendoorStartDate(new Date(stats.opendoor_start_date));
      } catch {
        // ignore
      }
    };
    loadWhiteListInfo();
  }, []);

  useEffect(() => {
    return () => {
      if (flowCancelRef.current) {
        flowCancelRef.current.cancelled = true;
      }
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
      if (prizePollingRef.current) {
        clearInterval(prizePollingRef.current);
      }
    };
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleBuyClick = async () => {
    if (!nft) return;
    setLoadingAction(true);
    setPurchaseError(null);
    try {
      // Проверка White List: если текущая дата меньше даты окончания — White List ещё не стартовал
      const wlStats = await apiClient.getWhiteListStats().catch(() => null);
      const whiteListStartDate = wlStats?.white_list_start_date
        ? new Date(wlStats.white_list_start_date).getTime()
        : null;
      if (whiteListStartDate !== null && Date.now() < whiteListStartDate) {
        setShowWhiteListModal(true);
        setLoadingAction(false);
        return;
      }
      // Проверка СБП: если White List уже окончен и СБП не привязан
      const status = await apiClient.getOnboardingStatus();
      if (!status.sbp_completed) {
        setShowSBPModal(true);
        setLoadingAction(false);
        return;
      }
      setPurchaseType('purchase');
      const clientToken = generateClientToken();
      await runPaymentFlow(apiClient.purchaseNFT(nft.id, clientToken), clientToken);
    } catch (error: any) {
      setPurchaseState('error');
      setPurchaseError(
        error?.response?.data?.message || 'Ошибка при создании платежа'
      );
    } finally {
      setLoadingAction(false);
    }
  };

  const handleShare = () => {
    if (!nft || !tg) return;
    const botUsername = resolveTelegramBotUsername();
    if (!botUsername) {
      // Bot username is not configured (VITE_TELEGRAM_BOT_USERNAME); a share link
      // would point to a non-existent bot, so skip sharing instead.
      return;
    }
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(
      `https://t.me/${botUsername}?startapp=nft_${nft.id}`
    )}&text=${encodeURIComponent(`Посмотри эту NFT: ${nft.title}! 🎨`)}`;
    tg.openTelegramLink(shareUrl);
  };

  // Reset timer-claim ref when switching to a different NFT
  const prevNftIdRef = useRef<string | null>(null);
  useEffect(() => {
    const nftId = nft?.id ?? null;
    if (prevNftIdRef.current !== nftId) {
      prevNftIdRef.current = nftId;
      timerClaimAttemptedRef.current = false;
    }
  }, [nft?.id]);

  /** Актуальный владелец с бэкенда — при «выкуплен» в state часто остаётся старый owner */
  useEffect(() => {
    if (!showPrizeIntercepted || !id) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await apiClient.getNFTDetail(id);
        if (!cancelled) setNft(detail);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPrizeIntercepted, id]);

  // Initialize claim state from NFT detail data
  useEffect(() => {
    if (!nft?.prize_claim_info) return;
    const info = nft.prize_claim_info;
    if (info.phase > 0) {
      setClaimPhase(info.phase);
      setIsMyClaimActive(info.is_my_claim);
      if (info.timer_ends_at) {
        setClaimTimerTarget(new Date(info.timer_ends_at).getTime());
        setClaimTimerTotalSeconds(info.timer_total_seconds ?? null);
      }
      if (info.prize_image_url) {
        setPrizeImageUrl(info.prize_image_url);
      }
    }
  }, [nft?.prize_claim_info]);

  // Tick for countdown display
  useEffect(() => {
    if (!claimTimerTarget || prizeAwarded) return;
    const id = setInterval(() => setTimerTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [claimTimerTarget, prizeAwarded]);

  // When timer hits 0, trigger backend to award prize immediately (avoids waiting for cron)
  useEffect(() => {
    if (!nft || prizeAwarded || prizeIntercepted || claimingPrize) return;
    if (!isOwner) return;
    if (claimPhase !== 1 && claimPhase !== 2 || !isMyClaimActive) return;
    if (!claimTimerTarget || timerClaimAttemptedRef.current) return;

    const timeLeft = calcTimeLeft(claimTimerTarget);
    if (timeLeft.total > 0) return;

    timerClaimAttemptedRef.current = true;
    setClaimingPrize(true);

    const doClaim = async () => {
      try {
        const result = await apiClient.claimNftPrize(Number(nft.id));
        if (result.success && result.phase === 3) {
          setPrizeAwarded(true);
          setAwardedDropNftId(result.drop_nft_id ?? null);
        }
      } catch {
        timerClaimAttemptedRef.current = false;
        // Fallback: polling will eventually get awarded status
      } finally {
        setClaimingPrize(false);
      }
    };
    doClaim();
  }, [nft, isOwner, claimPhase, isMyClaimActive, claimTimerTarget, prizeAwarded, prizeIntercepted, timerTick]);

  // Poll prize status while timer is active
  useEffect(() => {
    if (!nft || claimPhase === 0 || prizeAwarded || prizeIntercepted) return;
    // Уже не владелец, но claim в БД ещё «мой» — перекупка; экран «выкуплен» без опроса
    if (!isOwner && isMyClaimActive && claimPhase > 0 && claimPhase < 3) return;

    const poll = async () => {
      try {
        const status: PrizeStatusResponse = await apiClient.getPrizeStatus(Number(nft.id));
        if (status.prize_image_url) {
          setPrizeImageUrl(status.prize_image_url);
        }
        // NFT was sold — ownership lost
        if (status.is_owner === false) {
          setPrizeIntercepted(true);
          if (prizePollingRef.current) {
            clearInterval(prizePollingRef.current);
            prizePollingRef.current = null;
          }
          return;
        }
        if (status.awarded) {
          if (prizePollingRef.current) {
            clearInterval(prizePollingRef.current);
            prizePollingRef.current = null;
          }
          if (!status.is_owner || !status.is_my_claim) {
            setPrizeIntercepted(true);
          } else {
            setPrizeAwarded(true);
            setAwardedDropNftId(status.drop_nft_id ?? null);
          }
          return;
        }
        if (status.phase !== claimPhase) {
          setClaimPhase(status.phase);
          if (status.timer_ends_at) {
            setClaimTimerTarget(new Date(status.timer_ends_at).getTime());
            setClaimTimerTotalSeconds(status.timer_total_seconds ?? null);
          }
        }
      } catch {
        // ignore polling errors
      }
    };

    prizePollingRef.current = setInterval(poll, 5000);
    return () => {
      if (prizePollingRef.current) {
        clearInterval(prizePollingRef.current);
        prizePollingRef.current = null;
      }
    };
  }, [nft, isOwner, isMyClaimActive, claimPhase, prizeAwarded, prizeIntercepted]);

  const handleClaimPrize = async () => {
    if (!nft || claimingPrize) return;
    setClaimingPrize(true);
    try {
      const result = await apiClient.claimNftPrize(Number(nft.id));
      if (result.success) {
        setClaimPhase(result.phase);
        setIsMyClaimActive(true);
        if (result.timer_ends_at) {
          setClaimTimerTarget(new Date(result.timer_ends_at).getTime());
          setClaimTimerTotalSeconds(result.timer_total_seconds ?? null);
        }
        if (result.phase === 3) {
          setPrizeAwarded(true);
          setAwardedDropNftId(result.drop_nft_id);
        }
      }
    } catch (err) {
      console.error('Failed to claim prize:', err);
    } finally {
      setClaimingPrize(false);
    }
  };

  const handleUpgradeClick = async () => {
    if (!nft) return;
    setLoadingAction(true);
    setPurchaseError(null);
    setIsUpgradeModalOpen(false);
    try {
      const status = await apiClient.getOnboardingStatus();
      if (!status.sbp_completed) {
        setShowSBPModal(true);
        setLoadingAction(false);
        return;
      }
      setPurchaseType('upgrade');
      const clientToken = generateClientToken();
      await runPaymentFlow(apiClient.upgradeNFT(nft.id, undefined, clientToken), clientToken);
    } catch (error: any) {
      setPurchaseState('error');
      setPurchaseError(
        error?.response?.data?.message || 'Ошибка при создании платежа'
      );
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRandomReplace = async () => {
    try {
      const detail = await apiClient.getRandomNFT({ excludeOwned: true });
      navigate(`/nft/${detail.id}?random=1`, { replace: true });
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="nft-detail-page">
        <div className="nft-detail-header">
          <button className="nft-detail-back" onClick={handleBack} aria-label="Назад">
            <span className="nft-detail-back-inner">
              <img src="/back.png" alt="" />
            </span>
          </button>
        </div>
        <div className="nft-detail-loading">
          <div className="nft-detail-spinner" />
        </div>
      </div>
    );
  }

  if (error || !nft) {
    return (
      <div className="nft-detail-page">
        <div className="nft-detail-header">
          <button className="nft-detail-back" onClick={handleBack} aria-label="Назад">
            <span className="nft-detail-back-inner">
              <img src="/back.png" alt="" />
            </span>
          </button>
        </div>
        <div className="nft-detail-error">
          <p>{error || 'NFT не найден'}</p>
        </div>
      </div>
    );
  }

  if (purchaseState === 'creating' || purchaseState === 'waiting') {
    return (
      <div className="nft-detail-page">
        <div className="nft-purchase-waiting">
          <div className="nft-purchase-waiting-spinner" />
          <h2 className="nft-purchase-waiting-title">
            {purchaseState === 'creating' ? 'Создание платежа...' : 'Платёж выполняется'}
          </h2>
          <p className="nft-purchase-waiting-subtitle">
            {purchaseState === 'creating'
              ? 'Не закрывайте эту страницу'
              : 'Перевод по СБП может занять до 20 минут. После завершения операции NFT появится в разделе «Мои ху#ки».'}
          </p>
        </div>
      </div>
    );
  }

  if (purchaseState === 'success') {
    return (
      <div className="nft-detail-page">
        <div className="nft-purchase-success">
          <div className="nft-success-icon-wrap">
            <img src="/animation.gif" alt="" className="nft-success-animation" />
            <div className="nft-success-icon-circle">
              <img src="/nfticon.png" alt="" className="nft-success-icon-img" />
            </div>
          </div>
          <div className="nft-success-pill">
            Поздравляем! Вы в край аху#ли
          </div>
          <div className="nft-success-card">
            <img src={nft.image_url} alt={nft.title} className="nft-success-card-img" />
            <span className="nft-success-card-name">{nft.title}</span>
          </div>
          <p className="nft-purchase-success-subtitle">
            {purchaseType === 'upgrade'
              ? `NFT «${nft.title}» улучшена. Изменения уже в вашей коллекции`
              : `Теперь вы обладатель NFT «${nft.title}». Он уже доступен в разделе Мои ху#ки`}
          </p>
          <button
            className="nft-detail-btn primary button--accent"
            onClick={() => navigate('/my-nfts')}
          >
            Смотреть &laquo;Мои ху#ки&raquo;
          </button>
        </div>
      </div>
    );
  }

  if (purchaseState === 'error') {
    return (
      <div className="nft-detail-page">
        <div className="nft-detail-header">
          <button className="nft-detail-back" onClick={handleBack} aria-label="Назад">
            <span className="nft-detail-back-inner">
              <img src="/back.png" alt="" />
            </span>
          </button>
        </div>
        <div className="nft-purchase-error">
            <div className="nft-purchase-error-icon">✕</div>
            <h2 className="nft-purchase-error-title">Ошибка</h2>
            <p className="nft-purchase-error-subtitle">
              {purchaseError || 'Произошла ошибка при оплате'}
            </p>
            <button className="nft-detail-btn primary button--accent" onClick={resetPurchaseState}>
              Попробовать снова
            </button>
            <button className="nft-detail-btn secondary" onClick={handleBack}>
              Закрыть
            </button>
          </div>
      </div>
    );
  }

  // Prize intercepted — NFT was sold to another user
  if (showPrizeIntercepted) {
    return (
      <>
        <div className="nft-detail-page">
          <div className="prize-intercepted-top">
            <h2 className="prize-intercepted-title">Этот ху#к был выкуплен</h2>
            <p className="prize-intercepted-subtitle">
              Таймер и возможность получить приз были доступны только на момент владения
            </p>
          </div>

          <div className="nft-detail-image-container">
            {!imageLoaded && (
              <div className="nft-detail-image-placeholder">
                <div className="nft-detail-image-loader" />
              </div>
            )}
            <img
              src={nft.image_url}
              alt={nft.title}
              className={`nft-detail-image ${imageLoaded ? 'loaded' : ''}`}
              onLoad={() => setImageLoaded(true)}
            />
            <div className="nft-detail-image-overlays">
              <button
                type="button"
                className="nft-detail-overlay-btn"
                onClick={(e) => { e.stopPropagation(); handleBack(); }}
                aria-label="Назад"
              >
                <span className="nft-detail-overlay-circle">
                  <img src="/back.png" alt="" className="nft-detail-overlay-icon" />
                </span>
              </button>
              <button
                type="button"
                className="nft-detail-overlay-btn"
                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                aria-label="Поделиться"
              >
                <span className="nft-detail-overlay-circle">
                  <img src="/share.png" alt="" className="nft-detail-overlay-icon" />
                </span>
              </button>
            </div>
          </div>

          <div className="nft-detail-info-block">
            <div className="nft-detail-info-row">
              <h2 className="nft-detail-title">{nft.title}</h2>
              {nft.tier != null && (
                <span className="nft-detail-tier-chip">Тир {nft.tier}</span>
              )}
            </div>
            {nft.owner && (
              <p className="nft-detail-owner-line">
                <span className="nft-detail-owner-label">Владелец: </span>
                <span className="nft-detail-owner-name">{nft.owner.name}</span>
              </p>
            )}
            {nft.description && (
              <p className="nft-detail-description">{nft.description}</p>
            )}
          </div>

          {canShowBuyButton && (
            <div className="nft-detail-actions">
              <div className="nft-detail-buy-row">
                <button
                  type="button"
                  className="nft-detail-btn primary button--accent nft-detail-btn-buy"
                  onClick={handleBuyClick}
                  disabled={loadingAction}
                >
                  {loadingAction
                    ? 'Загрузка...'
                    : nft.price > 0
                      ? `Купить за ${nft.price.toLocaleString('ru-RU')} ₽`
                      : 'Купить бесплатно'}
                </button>
                {isFromRandom && (
                  <button
                    type="button"
                    className="nft-detail-replace-btn"
                    onClick={handleRandomReplace}
                    aria-label="Заменить"
                  >
                    <img src="/update.png" alt="" className="nft-detail-replace-icon" />
                  </button>
                )}
              </div>
            </div>
          )}
          {canShowContinueButton && (
            <div className="nft-detail-actions">
              <button
                type="button"
                className="nft-detail-btn primary button--accent nft-detail-btn-buy"
                onClick={handleContinuePurchase}
                disabled={loadingAction}
              >
                Продолжить покупку
              </button>
            </div>
          )}
        </div>

        <SBPModal
          isOpen={showSBPModal}
          onClose={() => setShowSBPModal(false)}
          pendingNftId={nft?.id}
          returnPathAfterSbp={nft?.id != null ? `/nft/${nft.id}` : null}
        />
        <WhiteListNotStartedModal
          isOpen={showWhiteListModal}
          onClose={() => setShowWhiteListModal(false)}
        />
      </>
    );
  }

  // Full-page timer view with progressive blur reveal (только пока пользователь — владелец NFT)
  if (!prizeAwarded && isOwner && isMyClaimActive && claimPhase > 0 && claimPhase < 3) {
    const timeLeft = claimTimerTarget ? calcTimeLeft(claimTimerTarget) : null;
    const hasTime = timeLeft && timeLeft.total > 0;
    // Drop timers may exceed an hour, so the whole remainder is shown as minutes: 180:00
    const minutesLeft = timeLeft ? timeLeft.days * 24 * 60 + timeLeft.hours * 60 + timeLeft.minutes : 0;

    // Phase length is set per drop in the admin panel; older API responses have no
    // total, so fall back to the durations this screen used to hardcode.
    const totalDurationMs = (claimTimerTotalSeconds || (claimPhase === 1 ? 300 : 120)) * 1000;
    const remainingShare = hasTime && timeLeft
      ? Math.min(1, timeLeft.total / totalDurationMs)
      : 0;
    const progress = 1 - remainingShare;

    // Prize is revealed step by step as the timer runs out, proportionally to its length
    // Phase 1: blur20 -> blur15 -> blur10 -> blur5 -> actual prize
    // Phase 2: blur5 -> actual prize
    let blurImage: string | null = null;
    let showActualPrize = false;

    if (claimPhase === 1) {
      if (remainingShare > 0.8) blurImage = '/present_blur20.png';
      else if (remainingShare > 0.6) blurImage = '/present_blur15.png';
      else if (remainingShare > 0.4) blurImage = '/present_blur10.png';
      else if (remainingShare > 0.2) blurImage = '/present_blur5.png';
      else showActualPrize = true;
    } else {
      if (remainingShare > 0.5) blurImage = '/present_blur5.png';
      else showActualPrize = true;
    }

    return (
      <div className="nft-detail-page prize-timer-page">
        <div className="prize-timer-header">
          <button type="button" className="prize-timer-back" onClick={handleBack} aria-label="Назад">
            <img src="/chevron-back.png" alt="" width={24} height={24} />
          </button>
        </div>

        <h1 className="prize-timer-title">Приз уже на подходе!</h1>

        {nft.tier != null && (
          <span className="prize-timer-tier">Тир {nft.tier}</span>
        )}

        <div className="prize-timer-progress-ring" style={{ '--progress': progress } as React.CSSProperties}>
          <svg className="prize-timer-progress-svg" viewBox="0 0 240 240" aria-hidden>
            <defs>
              <clipPath id={prizeRingClipId}>
                <circle cx="120" cy="120" r="120" />
              </clipPath>
              {/* Размытие контура: без видимой «ленты» трека, только мягкое свечение внутрь круга (clip + overflow) */}
              <filter
                id={prizeRingInnerGlowId}
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
                colorInterpolationFilters="sRGB"
              >
                <feGaussianBlur in="SourceGraphic" stdDeviation="4.5" result="blur" />
                <feColorMatrix
                  in="blur"
                  type="matrix"
                  values="1.35 0 0 0 1.15
                          0 1.1 0 0 1
                          0 0 1.25 0 1.15
                          0 0 0 1.45 0"
                  result="glow"
                />
              </filter>
              <linearGradient id={prizeProgressGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#EC005F" />
                <stop offset="93.1%" stopColor="#FFFFFF" />
              </linearGradient>
            </defs>
            <g clipPath={`url(#${prizeRingClipId})`}>
              <g filter={`url(#${prizeRingInnerGlowId})`}>
                <path
                  className="prize-timer-progress-track"
                  d="M 120,5 A 115,115 0 0,1 120,235 A 115,115 0 0,1 120,5"
                  fill="none"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </g>
              <path
                className="prize-timer-progress-path"
                d="M 120,5 A 115,115 0 0,1 120,235 A 115,115 0 0,1 120,5"
                fill="none"
                stroke={`url(#${prizeProgressGradientId})`}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray="722.57"
                style={{ strokeDashoffset: 722.57 * (1 - progress) }}
              />
            </g>
          </svg>
          <div className={`prize-timer-image-wrap${showActualPrize && prizeImageUrl ? ' prize-timer-image-wrap--revealed' : ''}`}>
          {showActualPrize && prizeImageUrl ? (
            <img src={prizeImageUrl} alt="Приз" className="prize-timer-image prize-timer-image--revealed" />
          ) : (
            <div className="prize-timer-blur-wrap">
              <img
                src={blurImage || '/present_blur20.png'}
                alt=""
                className="prize-timer-image"
              />
              <img src="/prize-box.png" alt="" className="prize-timer-gift-icon" />
            </div>
          )}
          </div>
        </div>

        {hasTime ? (
          <div className="prize-timer-countdown">
            <span className="prize-timer-digit">{pad(minutesLeft)}</span>
            <span className="prize-timer-colon">:</span>
            <span className="prize-timer-digit">{pad(timeLeft.seconds)}</span>
          </div>
        ) : (
          <p className="prize-timer-processing">Обработка...</p>
        )}

        <p className="prize-timer-hint">
          Когда таймер закончится, приз станет вашим — если NFT не перекупят
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="nft-detail-page">
        <div className="nft-detail-image-container">
            {!imageLoaded && (
              <div className="nft-detail-image-placeholder">
                <div className="nft-detail-image-loader" />
              </div>
            )}
            <img
              src={nft.image_url}
              alt={nft.title}
              className={`nft-detail-image ${imageLoaded ? 'loaded' : ''}`}
              onLoad={() => setImageLoaded(true)}
            />
            <div className="nft-detail-image-overlays">
              <button
                className="nft-detail-overlay-btn"
                onClick={(e) => { e.stopPropagation(); handleBack(); }}
                aria-label="Назад"
              >
                <span className="nft-detail-overlay-circle">
                  <img src="/back.png" alt="" className="nft-detail-overlay-icon" />
                </span>
              </button>
              <button
                className="nft-detail-overlay-btn"
                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                aria-label="Поделиться"
              >
                <span className="nft-detail-overlay-circle">
                  <img src="/share.png" alt="" className="nft-detail-overlay-icon" />
                </span>
              </button>
            </div>
            <div className="detail-media-bottom">
              <div className="detail-penki">
                <span className="penka-icon penka-icon--md" aria-hidden>
                  <img src="/uym-mark.png" alt="" />
                </span>
                <div className="detail-penki-text">
                  <span className="detail-penki-value">{formatPenki(dailyPenki)}</span>
                  <span className="detail-penki-unit">{pluralPenka()}</span>
                </div>
              </div>
            </div>
            <div className="nft-detail-badges">
              {canClaimPrize && (
                <span className="nft-detail-badge prize">
                  <img src="/prize-s.png" alt="" className="nft-detail-badge-icon" />
                  <span className="nft-detail-badge-text">Призовая хуй#ня</span>
                </span>
              )}
              {nft.tier === 14 && <span className="nft-detail-badge granite">Гранит</span>}
            </div>
          </div>

          <div className="nft-detail-info-block">
            <div className="nft-detail-info-row">
              <h2 className="nft-detail-title">{nft.title}</h2>
              {nft.tier != null && (
                <span className="nft-detail-tier-chip">Тир {nft.tier}</span>
              )}
            </div>
            {nft.owner && (
              <p className="nft-detail-owner-line">
                <span className="nft-detail-owner-label">Владелец: </span>
                <span className="nft-detail-owner-name">{nft.owner.name}</span>
              </p>
            )}
            {nft.description && (
              <p className="nft-detail-description">{nft.description}</p>
            )}
          </div>

          <div className="nft-detail-actions">
            {prizeAwarded && !prizeIntercepted && (
              <div className="nft-detail-prize-awarded">
                <p className="prize-awarded-text">Приз получен!</p>
                <button
                  className="nft-detail-btn button--accent"
                  onClick={() => awardedDropNftId && navigate(`/prizes/${awardedDropNftId}`)}
                >
                  Смотреть приз
                </button>
              </div>
            )}

            {/* Claim button: phase 0 (no claim yet) OR new owner who can advance phase */}
            {canClaimPrize && !prizeAwarded && !isMyClaimActive && (
              <button
                className="nft-detail-btn button--accent"
                onClick={handleClaimPrize}
                disabled={claimingPrize}
              >
                {claimingPrize ? 'Отправка...' : 'Забрать приз'}
              </button>
            )}

            {canShowBuyButton && (
              <div className="nft-detail-buy-row">
                <button
                  className="nft-detail-btn primary button--accent nft-detail-btn-buy"
                  onClick={handleBuyClick}
                  disabled={loadingAction}
                >
                  {loadingAction
                    ? 'Загрузка...'
                    : nft.price > 0
                      ? `Купить за ${nft.price.toLocaleString('ru-RU')} ₽`
                      : 'Купить бесплатно'}
                </button>
                {isFromRandom && (
                  <button
                    className="nft-detail-replace-btn"
                    onClick={handleRandomReplace}
                    aria-label="Заменить"
                  >
                    <img src="/update.png" alt="" className="nft-detail-replace-icon" />
                  </button>
                )}
              </div>
            )}

            {canShowContinueButton && (
              <button
                className="nft-detail-btn primary button--accent nft-detail-btn-buy"
                onClick={handleContinuePurchase}
                disabled={loadingAction}
              >
                Продолжить покупку
              </button>
            )}

            {nft.can_upgrade && (
              <button
                className="nft-detail-btn button--primary primary nft-detail-btn-buy"
                onClick={() => setIsUpgradeModalOpen(true)}
              >
                Улучшить
              </button>
            )}
          </div>
      </div>

      <SBPModal
        isOpen={showSBPModal}
        onClose={() => setShowSBPModal(false)}
        pendingNftId={nft?.id}
        returnPathAfterSbp={nft?.id != null ? `/nft/${nft.id}` : null}
      />
      <WhiteListNotStartedModal
        isOpen={showWhiteListModal}
        onClose={() => setShowWhiteListModal(false)}
      />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        nft={nft}
        onConfirm={handleUpgradeClick}
      />
    </>
  );
};
